import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting.
 * 
 * This function is critical for security when rendering user-generated or admin-generated
 * HTML content. It removes all potentially dangerous elements (scripts, event handlers, etc.)
 * while allowing safe formatting tags needed for medical education content.
 * 
 * Use this whenever rendering HTML with dangerouslySetInnerHTML.
 * 
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML safe for rendering
 */
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      // Text formatting
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span', 'div',
      // Headings
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // Lists
      'ul', 'ol', 'li',
      // Tables (important for medical content)
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      // Other safe elements
      'blockquote', 'code', 'pre', 'sub', 'sup'
    ],
    ALLOWED_ATTR: [
      // Allow class for styling
      'class',
      // Allow data attributes for custom functionality
      'data-*'
    ],
    // Remove any protocol-based attributes that could be dangerous
    ALLOW_DATA_ATTR: true,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
};

